import { Localize } from '@/i18n/Localize';
import { sharedPortuguese } from '@/i18n/shared';
import { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

/**
 * Root route `errorElement`. React Router (the data router that vite-react-ssg uses)
 * catches any error thrown while rendering a route and renders this instead of its
 * bare-bones default "Unexpected Application Error!" screen.
 *
 * Crucially, React-Router-caught errors never reach `window.onerror`, so Sentry's
 * global handlers don't see them — we report them here explicitly so route-render
 * crashes stop being invisible in Sentry.
 */
const RouteError = () => {
  const error = useRouteError();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Sentry is initialised (client-only) in main.tsx; the dynamic import returns the
    // same cached module instance, so captureException reports to the live client.
    import('@sentry/react')
      .then(({ captureException }) => captureException(error))
      .catch(() => {
        /* Sentry unavailable (blocked, dev, etc.) — swallow. */
      });
  }, [error]);

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : String(error);

  return <Localize messages={sharedPortuguese}>{(
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center max-w-md">
        <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
        <p className="text-muted-foreground mb-6">
          We're sorry, but something unexpected happened. Please try refreshing the page.
        </p>
        <div className="flex gap-4 justify-center">
          <Button onClick={() => window.location.reload()}>Refresh</Button>
          <Button variant="outline" onClick={() => (window.location.href = '/')}>
            Go Home
          </Button>
        </div>
        {import.meta.env.DEV && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Error Details
            </summary>
            <pre className="mt-2 text-xs bg-muted p-4 rounded overflow-auto">{detail}</pre>
          </details>
        )}
      </div>
    </div>
  )}</Localize>;
};

export default RouteError;
